'use client'

import {useCallback, useEffect, useRef, useState} from 'react'
import {BellIcon, BriefcaseIcon, ChartBarIcon, DocumentTextIcon, UserCircleIcon, XMarkIcon} from '@heroicons/react/24/outline'
import {User} from "../../../../../api/accessControl/User.ts";
import Shell from "../../../../../components/specific/Shell.tsx";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {BackendApi} from "../../../../../api/api.ts";
import {
    useBusiness__myBusiness,
    useMe__business__editBaseBusinessInformationMutation,
} from "../../../../../generated/blueprint.ts";
import {QueryKey} from "../../../../../api/queryKeys.ts";
import {Tab, Tabs} from "../../../../../components/general/Tabs.tsx";
import {RoleEnumToString} from "../../../../../api/accessControl/Role.ts";
import {SecondaryButton} from "../../../../../components/general/SecondaryButton.tsx";
import {PlusIcon} from "@heroicons/react/16/solid/index";
import {signOut} from "firebase/auth";
import {auth} from "../../../../../firebaseApp.ts";
import NarrowDrawer from "../../../../../components/general/NarrowDrawer.tsx";
import {BankAccount, BankAccountsTable} from "./BankAccountsTable.tsx";
import {PrimaryButton} from "../../../../../components/general/PrimaryButton.tsx";
import {PaginationState} from "../../../../../components/table/CreateTable.tsx";
import {InsightsSettingsTab} from "./InsightsSettingsTab.tsx";
import {NotificationSettingsTab} from "./NotificationSettingsTab.tsx";
import {ImageUpload} from "../../../../../components/general/ImageUpload.tsx";
import {InfoTooltip} from "../../../../../components/general/InfoTooltip.tsx";
import {EditableSettingRow} from "../../../../../components/form/EditableSettingRow.tsx";
import {validateEmail, validateVatNumber, validateWebsite} from "../../../../../components/form/validation.ts";
import {debounce} from "@mui/material";
import {useNotifications} from "../../../../../lib/NotificationContext.tsx";
import AddressField, {AddressFieldValue} from "../../../../../components/general/AddressField.tsx";
import {useSearchParams} from "react-router-dom";
import {getLabelForLegalEntityType} from "../../../../../types/enums.ts";
import {SsoAccountLinkingSection} from "../../../../../components/auth/SsoAccountLinkingSection.tsx";

function SettingRow({name, children, full, tooltip, showError}: { name: string, children: any, full?: boolean, tooltip?: string, showError?: boolean }) {
    return (
        <div className={("justify-between bg-gray-50 p-3 rounded-md" + (!full ? ' sm:flex' : '') + (showError ? ' ring-1 ring-red-300' : ''))}>
            <dt className="font-medium text-gray-900 sm:w-64  sm:pr-6 flex items-center gap-2">
                {name}
                {tooltip && <InfoTooltip text={tooltip} position="right" />}
            </dt>
            <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0">
                {children}
            </dd>
        </div>
    )
}

interface BusinessSettings {
    OWNER_BANK_ACCOUNTS: {
        id: string,
        relation_name: string,
        relation_iban: string,
    }[],
    BUSINESS_ADDRESS_STREET_NAME?: string
    BUSINESS_ADDRESS_HOUSE_NUMBER?: string
    BUSINESS_ADDRESS_CITY_NAME?: string
    BUSINESS_ADDRESS_POSTAL_CODE?: string
    BUSINESS_ADDRESS_COUNTRY?: string
    BUSINESS_LOGO_URL?: string
    SALES_INVOICE_CONTACT_EMAIL?: string
}

export default function EntrepreneurSettingsPage({currentUser}: { currentUser: User }) {
    const [searchParams] = useSearchParams()
    const tabParam = searchParams.get('tab')
    const tabIndex = tabParam ? parseInt(tabParam) : 0
    const defaultActiveTab = !isNaN(tabIndex) ? tabIndex : 0
    const missingFieldsParam = searchParams.get('missingFields')
    const missingFields = missingFieldsParam ? missingFieldsParam.split(',') : []

    function shouldShowError(fieldName: string, hasValue: boolean): boolean {
        return missingFields.includes(fieldName) && !hasValue
    }

    const [isDrawerForBankAccountSelectionOpen, setIsDrawerForBankAccountSelectionOpen] = useState(false)
    const [relations, setRelations] = useState<any[]>([])
    const [businessSettings, setBusinessSettings] = useState<BusinessSettings>()
    const [selectedBankAccounts, setSelectedBankAccounts] = useState([])
    const tablePaginationState = useState<PaginationState<BankAccount>>();
    const api = new BackendApi(currentUser.getToken())
    const {dispatch} = useNotifications()
    const queryClient = useQueryClient()

    const {isPending: isLoading, data: business} = useBusiness__myBusiness(api, {queryKey: QueryKey.Business})

    async function fetchBusinessLogo(): Promise<string | null> {
        try {
            const data = await api.Me__business__logo({});
            const base64Data = `data:${data.content_type};base64,${data.file_data}`;
            return base64Data;
        } catch (error) {
            return null;
        }
    }

    const {data: logoUrl} = useQuery({
        queryKey: [QueryKey.BusinessLogo],
        queryFn: fetchBusinessLogo,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    })

    async function load() {
        const res = await api.Business__relations__allRelations({})
        setRelations(res.relations)


        const settingsApiResponse = await api.Me__business__settings({})
        const settings = settingsApiResponse.settings as BusinessSettings
        setSelectedBankAccounts(settings?.OWNER_BANK_ACCOUNTS || [])

        setBusinessSettings(settings)
    }

    useEffect(() => {
        load();
    }, []);



    const debouncedUpdateBusinessSettingRef = useRef(
        debounce(async (setting: string, value: any, dispatchFn: (notification: any) => void) => {
            try {
                await api.Me__business__updateSetting({
                    setting_name: setting,
                    setting_value: value,
                });
            } catch (error) {
                dispatchFn({
                    title: 'Fout bij updaten van instelling',
                    description: 'Er is iets misgegaan. Probeer het opnieuw.',
                    type: 'error',
                });
            }
        }, 1000)
    );


    const handleBusinessAddressSave = useCallback(async (address: AddressFieldValue) => {
        try {
            await Promise.all([
                api.Me__business__updateSetting({
                    setting_name: 'BUSINESS_ADDRESS_STREET_NAME',
                    setting_value: address.street_name,
                }),
                api.Me__business__updateSetting({
                    setting_name: 'BUSINESS_ADDRESS_HOUSE_NUMBER',
                    setting_value: address.house_number,
                }),
                api.Me__business__updateSetting({
                    setting_name: 'BUSINESS_ADDRESS_CITY_NAME',
                    setting_value: address.city_name,
                }),
                api.Me__business__updateSetting({
                    setting_name: 'BUSINESS_ADDRESS_POSTAL_CODE',
                    setting_value: address.postal_code,
                }),
                api.Me__business__updateSetting({
                    setting_name: 'BUSINESS_ADDRESS_COUNTRY',
                    setting_value: address.country,
                }),
            ]);
            await currentUser.businessSettings.refresh();
            setBusinessSettings((prev: any) => {
                return {
                    ...prev,
                    BUSINESS_ADDRESS_STREET_NAME: address.street_name,
                    BUSINESS_ADDRESS_HOUSE_NUMBER: address.house_number,
                    BUSINESS_ADDRESS_CITY_NAME: address.city_name,
                    BUSINESS_ADDRESS_POSTAL_CODE: address.postal_code,
                    BUSINESS_ADDRESS_COUNTRY: address.country,
                }
            });
        } catch (error) {
            dispatch({
                title: 'Fout bij updaten van instelling',
                description: 'Er is iets misgegaan. Probeer het opnieuw.',
                type: 'error',
            });
            throw error;
        }
    }, [api, dispatch, currentUser]);

    const {mutateAsync: editBaseBusinessInformation} = useMe__business__editBaseBusinessInformationMutation(api, {
        invalidates: [QueryKey.Business],
    });

    const handleBusinessFieldSave = useCallback(async (field: 'name' | 'website' | 'vat_number', value: string) => {
        try {
            await editBaseBusinessInformation({
                name: field === 'name' ? value : business.name,
                website: field === 'website' ? value : business.website,
                vat_number: field === 'vat_number' ? value : business.vat_number
            });
        } catch (error) {
            dispatch({
                title: 'Fout bij updaten van instelling',
                description: 'Er is iets misgegaan. Probeer het opnieuw.',
                type: 'error',
            });
            throw error;
        }
    }, [editBaseBusinessInformation, dispatch, business]);


    function CreateDebouncedOnSettingChangeFn(setting: string) {
        return (value: any) => {
            try {
                setBusinessSettings((prev: BusinessSettings | undefined) => {
                    return {...prev, [setting]: value} as BusinessSettings
                })
                debouncedUpdateBusinessSettingRef.current(setting, value, dispatch);
            } catch (error) {
                console.error('Error in CreateDebouncedOnSettingChangeFn:', error);
                dispatch({
                    title: 'Fout bij updaten van instelling',
                    description: 'Er is iets misgegaan. Probeer het opnieuw.',
                    type: 'error',
                });
            }
        }
    }

    function getBusinessSetting(name: string, businessSettings: any) {
        const value = businessSettings?.[name]

        if (businessSettings && value === undefined)
            throw new Error(`Setting ${name} is not present in business settings`)

        return value
    }

    function getBusinessAddress(businessSettings: any): AddressFieldValue {
        return {
            street_name: businessSettings?.BUSINESS_ADDRESS_STREET_NAME || '',
            house_number: businessSettings?.BUSINESS_ADDRESS_HOUSE_NUMBER || '',
            city_name: businessSettings?.BUSINESS_ADDRESS_CITY_NAME || '',
            postal_code: businessSettings?.BUSINESS_ADDRESS_POSTAL_CODE || '',
            country: businessSettings?.BUSINESS_ADDRESS_COUNTRY || 'NL'
        }
    }

    function checkAddress(businessSettings: any): boolean {
        return !!(
            businessSettings?.BUSINESS_ADDRESS_STREET_NAME &&
            businessSettings?.BUSINESS_ADDRESS_HOUSE_NUMBER &&
            businessSettings?.BUSINESS_ADDRESS_CITY_NAME &&
            businessSettings?.BUSINESS_ADDRESS_POSTAL_CODE
        )
    }

    const handleInvoiceContactEmailSave = useCallback(async (email: string) => {
        try {
            await api.Me__business__updateSetting({
                setting_name: 'SALES_INVOICE_CONTACT_EMAIL',
                setting_value: email.trim(),
            });
            await currentUser.businessSettings.refresh();
            setBusinessSettings((prev: any) => ({...prev, SALES_INVOICE_CONTACT_EMAIL: email.trim()}));
        } catch (error) {
            dispatch({
                title: 'Fout bij updaten van instelling',
                description: 'Er is iets misgegaan. Probeer het opnieuw.',
                type: 'error',
            });
            throw error;
        }
    }, [api, dispatch, currentUser]);

    async function handleLogoUpload(file: File) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.postFormData('me/business/upload-logo', formData);
        if (!response.ok) {
            throw new Error('Failed to upload logo');
        }
        await load();
        currentUser.businessSettings.refresh();
        queryClient.invalidateQueries({ queryKey: [QueryKey.BusinessLogo] });
    }


    return (
        <>
            <NarrowDrawer open={isDrawerForBankAccountSelectionOpen} title={'Selecteer een bankrekening'}
                          updateOpenState={setIsDrawerForBankAccountSelectionOpen}>
                {relations && <BankAccountsTable data={relations}
                                                 tablePaginationState={tablePaginationState}
                                                 onAddClick={(account) => {
                                                     console.log(selectedBankAccounts)
                                                     if (selectedBankAccounts.find((account_) => account_.id === account.id))
                                                         return

                                                     setSelectedBankAccounts((prev) => [
                                                         ...prev,
                                                         account
                                                     ])
                                                 }
                                                 }/>}
                <p className='text-sm text-gray-600 mt-2 text-right'>{relations?.length} bankrekeningen gevonden</p>

                {!!selectedBankAccounts.length &&
                    <h3 className={'text-md font-gray-900 py-3 pt-6'}>Geselecteerde bankrekeningen</h3>}
                <div className='my-3 rounded-md  w-full'>
                    {selectedBankAccounts?.map((account) => (
                        <div className="flex justify-between bg-gray-100 p-3 mb-2 rounded-md">
                            <div>
                                <div>
                                    <p>{account.relation_name}</p>
                                    <p className={'font-mono'}>{account.relation_iban}</p>
                                </div>

                            </div>

                            <XMarkIcon className="h-6 w-6 text-gray-400"
                                       onClick={() => setSelectedBankAccounts((prev) => prev.filter((account_) => account_.id !== account.id))}/>

                        </div>
                    ))}
                    {!selectedBankAccounts && (
                        <div className="flex justify-between bg-gray-100 p-3 mb-2 rounded-md">
                            <p>Nog geen bankrekeningen toegevoegd</p>
                        </div>
                    )}
                </div>
                <PrimaryButton title={'Klaar'} onClick={() => {
                    api.Me__business__updateSetting({
                        setting_name: "OWNER_BANK_ACCOUNTS",
                        setting_value: selectedBankAccounts
                    })
                    setIsDrawerForBankAccountSelectionOpen(false)
                }
                }/>
            </NarrowDrawer>

            <Shell currentUser={currentUser} isLoading={false} title="Instellingen">
                <div className="mt-12 max-w-5xl">
                    <Tabs defaultActiveTab={defaultActiveTab}>
                        <Tab label={'Basis'} icon={UserCircleIcon}>
                            <div>
                                <dl className="space-y-3 text-sm leading-6">
                                    <h3 className="text-xl font-gray-900 py-3 pt-6">Gegevens over jouw account</h3>
                                    <SettingRow name="Volledige naam">
                                        <div className="text-gray-900">{currentUser.getFullName()}</div>
                                    </SettingRow>
                                    <SettingRow name="E-mailadres">
                                        <div className="text-gray-900">{currentUser.email}</div>
                                    </SettingRow>
                                    <SettingRow name="Accounttype">
                                        <div className="text-gray-900">{RoleEnumToString(currentUser.role)}</div>
                                    </SettingRow>

                                </dl>

                                <SsoAccountLinkingSection/>

                                <button className="text-base bg-primary text-white px-4 py-2 rounded-md mt-12"
                                        onClick={async () => {
                                            await signOut(auth).then(() => {
                                                window.location.replace('/')
                                            })

                                        }}>Uitloggen
                                </button>
                            </div>
                        </Tab>
                        <Tab label={'Bedrijf'} icon={BriefcaseIcon}>
                            <div>
                                <dl className="space-y-3  text-sm leading-6">
                                    <h3 className="text-xl font-gray-900 py-3 pt-6">Gegevens over je bedrijf</h3>
                                    <EditableSettingRow
                                        name="Bedrijfsnaam" 
                                        value={business?.name}
                                        onSave={(value) => handleBusinessFieldSave('name', value)}
                                        placeholder="Bedrijfsnaam"
                                        validation={(value) => !!value}
                                        validationErrorMessage="Bedrijfsnaam is verplicht"
                                        showError={shouldShowError('name', !!business?.name)}
                                    />
                                    <SettingRow 
                                        name="Bedrijfsadres"
                                        showError={shouldShowError('address', checkAddress(businessSettings))}
                                    >
                                        <AddressField
                                            label=""
                                            value={getBusinessAddress(businessSettings)}
                                            onChange={handleBusinessAddressSave}
                                            placeholder="Bedrijfsadres"
                                            asText={true}
                                        />
                                    </SettingRow>
                                    <EditableSettingRow
                                        name="Website"
                                        value={business?.website}
                                        onSave={(value) => handleBusinessFieldSave('website', value)}
                                        inputType="url"
                                        validation={validateWebsite}
                                        validationErrorMessage="Ongeldige bedrijfsadres"
                                        placeholder="www.website.com"
                                        showError={shouldShowError('website', !!business?.website)}
                                    />
                                    <EditableSettingRow
                                        name="BTW nummer"
                                        value={business?.vat_number}
                                        onSave={(value) => handleBusinessFieldSave('vat_number', value)}
                                        placeholder="NL123456789B01"
                                        validation={validateVatNumber}
                                        validationErrorMessage="Ongeldig BTW-nummer"
                                        showError={shouldShowError('vat_number', !!business?.vat_number)}
                                    />
                                    <SettingRow name="KvK nummer">
                                        <div className="text-gray-900">{business?.registration_number}</div>
                                    </SettingRow>
                                    <SettingRow name="Aantal FTE's">
                                        <div className="text-gray-900">{business?.fte_amount}</div>
                                    </SettingRow>
                                    <SettingRow name="Branche">
                                        <div className="text-gray-900">{business?.industry}</div>
                                    </SettingRow>
                                    <SettingRow name="Entiteit">
                                        <div className="text-gray-900">{getLabelForLegalEntityType(business?.legal_entity_type)}</div>
                                    </SettingRow>
                                    <SettingRow name="PocketCFO-analyse startdatum">
                                        <div className="text-gray-900">{
                                            business?.transaction_history_cutoff_date?.cutoff_date &&
                                            new Date(business?.transaction_history_cutoff_date?.cutoff_date).toDateString()
                                        }</div>
                                    </SettingRow>
                                </dl>
                                <dl className="space-y-3 text-sm leading-6 mt-8">
                                    <EditableSettingRow
                                        name="E-mailadres voor facturen"
                                        value={businessSettings?.SALES_INVOICE_CONTACT_EMAIL || ''}
                                        onSave={handleInvoiceContactEmailSave}
                                        inputType="email"
                                        placeholder={currentUser.email}
                                        validation={(value) => value.trim() === '' || validateEmail(value)}
                                        validationErrorMessage="Ongeldig e-mailadres"
                                    />
                                </dl>
                                <dl className="space-y-3 text-sm leading-6 mt-8">
                                    <SettingRow
                                     name="Logo van je bedrijf" 
                                     full={true}
                                     tooltip="We gebruiken je logo als je verkoopfacturen aanmaakt"
                                     showError={shouldShowError('logo', !!logoUrl)}>
                                        <ImageUpload
                                            currentImageUrl={logoUrl || null}
                                            onUpload={handleLogoUpload}
                                            uploadButtonText="Logo uploaden"
                                            changeButtonText="Logo wijzigen"
                                            imageAlt="Bedrijfslogo"
                                            sizeHint="PNG, JPG, SVG - Max 5 MB"
                                        />
                                    </SettingRow>
                                </dl>
                                <dl className="space-y-3 text-sm leading-6 mt-8">
                                    <h3 className="text-xl font-gray-900 py-3 pt-6 ">Eigenaar</h3>
                                    <SettingRow 
                                        name="Bankrekeningen van de eigenaar" 
                                        full={true}
                                    >

                                        <div className='my-3 rounded-md  w-full'>
                                            {selectedBankAccounts?.map((account) => (
                                                <div className="flex justify-between bg-gray-100 p-3 mb-2 rounded-md">
                                                    <div>
                                                        <div>
                                                            <p>{account.relation_name}</p>
                                                            <p className={'font-mono'}>{account.relation_iban}</p>
                                                        </div>

                                                    </div>

                                                    <XMarkIcon
                                                        className="h-6 w-6 text-gray-400 cursor-pointer hover:bg-gray-200 rounded-full p-1"
                                                        onClick={
                                                            () => {
                                                                setSelectedBankAccounts((prev) => {
                                                                    const newState = prev.filter((account_) => account_.relation_iban !== account.relation_iban)

                                                                    api.Me__business__updateSetting({
                                                                        setting_name: "OWNER_BANK_ACCOUNTS",
                                                                        setting_value: newState
                                                                    })

                                                                    return newState
                                                                })

                                                                load()
                                                            }
                                                        }/>

                                                </div>
                                            ))}
                                            {!selectedBankAccounts && (
                                                <div className="flex justify-between bg-gray-100 p-3 mb-2 rounded-md">
                                                    <p>Nog geen bankrekeningen toegevoegd</p>

                                                </div>
                                            )}
                                            <div>
                                                <SecondaryButton title={'Toevoegen'} icon={PlusIcon}
                                                                 onClick={() => setIsDrawerForBankAccountSelectionOpen(true)}/>
                                            </div>
                                        </div>


                                    </SettingRow>
                                    <SettingRow name="Maandelijkse budgetten" full={true}>
                                        <div className="flex flex-col gap-4 w-full">
                                            <p className="text-xs text-gray-600">
                                                Wij checken jouw uitgaven een keer per dag. Je krijgt een melding
                                                wanneer je 80% van je budget hebt bereikt,<br/> en nog een melding
                                                wanneer je over je budget heen gaat.
                                            </p>
                                            <div className="flex flex-col sm:flex-row gap-6 w-full">
                                                <div className="flex-1">
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Uitgaven
                                                    </label>
                                                    <div className="flex items-center gap-x-2">
                                                        <span>€</span>
                                                        <input
                                                            type="number"
                                                            className="block rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                            value={getBusinessSetting('MONTHLY_OUTFLOW_BUDGET', businessSettings) || ''}
                                                            onChange={(e) => CreateDebouncedOnSettingChangeFn('MONTHLY_OUTFLOW_BUDGET')(Number(e.target.value))}
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    </SettingRow>
                                </dl>
                            </div>
                        </Tab>
                        <Tab label={'Insights'} icon={ChartBarIcon}>
                            <InsightsSettingsTab
                                currentUser={currentUser}
                                businessSettings={businessSettings}
                                CreateOnSettingChangeFn={CreateDebouncedOnSettingChangeFn}
                                getBusinessSetting={getBusinessSetting}
                            />
                        </Tab>
                        <Tab label={'Meldingen'} icon={BellIcon}>
                            <NotificationSettingsTab currentUser={currentUser}/>
                        </Tab>
                    </Tabs>
                </div>

            </Shell>
        </>
    )
}
